/**
 * Enhanced EdgeAgentDetector with WASM support
 * Provides both pattern-based and WASM-based detection
 */

import { detectAgentWithWasm, initWasm, isWasmAvailable, setWasmBaseUrl } from './wasm-loader';

type DetectionInput = {
  userAgent?: string;
  ip?: string;
  ipAddress?: string;
  headers?: Record<string, string>;
  url?: string;
  method?: string;
  timestamp?: Date;
};

type DetectionResult = {
  isAgent: boolean;
  confidence: number;
  detectedAgent?: {
    type: string;
    name: string;
  };
  reasons: string[];
  verificationMethod?: string;
  forgeabilityRisk?: 'low' | 'medium' | 'high';
  timestamp: Date;
};

type EventHandler = (...args: any[]) => void;

// Known AI agent patterns
const AI_AGENT_PATTERNS = [
  { pattern: /chatgpt-user/i, type: 'chatgpt', name: 'ChatGPT' },
  { pattern: /claude-web/i, type: 'claude', name: 'Claude' },
  { pattern: /perplexitybot/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /perplexity-user/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /perplexity-ai/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /perplexity/i, type: 'perplexity', name: 'Perplexity' },
  { pattern: /bingbot/i, type: 'bing', name: 'Bing AI' },
  { pattern: /anthropic-ai/i, type: 'anthropic', name: 'Anthropic' },
];

// Known cloud provider IP ranges (simplified)
const CLOUD_PROVIDERS = {
  aws: ['54.', '52.', '35.', '18.', '3.'],
  gcp: ['35.', '34.', '104.', '107.', '108.'],
  azure: ['13.', '20.', '40.', '52.', '104.'],
};

/**
 * EdgeAgentDetector with WASM enhancement
 */
export class EdgeAgentDetectorWithWasm {
  private wasmEnabled: boolean = false;
  private initPromise: Promise<void> | null = null;
  private baseUrl: string | null = null;

  constructor(private enableWasm: boolean = true) {}

  /**
   * Set the base URL for WASM loading in Edge Runtime
   */
  setBaseUrl(url: string) {
    this.baseUrl = url;
    setWasmBaseUrl(url);
  }

  /**
   * Initialize the detector (including WASM if enabled)
   */
  async init(): Promise<void> {
    if (!this.enableWasm) {
      this.wasmEnabled = false;
      return;
    }

    if (this.initPromise) {
      await this.initPromise;
      return;
    }

    this.initPromise = (async () => {
      try {
        // Try to initialize WASM
        const wasmAvailable = await isWasmAvailable();
        this.wasmEnabled = wasmAvailable;
        
        if (this.wasmEnabled) {
          console.log('[AgentShield] WASM detection enabled');
        } else {
          console.log('[AgentShield] WASM not available, using pattern detection');
        }
      } catch (error) {
        console.error('[AgentShield] Failed to initialize WASM:', error);
        this.wasmEnabled = false;
      }
    })();

    await this.initPromise;
  }

  /**
   * Pattern-based detection (fallback)
   */
  private async patternDetection(input: DetectionInput): Promise<DetectionResult> {
    const reasons: string[] = [];
    let detectedAgent: { type: string; name: string } | undefined;
    let verificationMethod: string | undefined;
    let confidence = 0;

    const headers = input.headers || {};
    const normalizedHeaders: Record<string, string> = {};

    // Normalize header names to lowercase
    for (const [key, value] of Object.entries(headers)) {
      normalizedHeaders[key.toLowerCase()] = value;
    }

    // Check for HTTP Message Signatures (highest confidence)
    const signaturePresent = !!(
      normalizedHeaders['signature'] || normalizedHeaders['signature-input']
    );
    const signatureAgent = normalizedHeaders['signature-agent'];

    if (signatureAgent?.includes('chatgpt.com')) {
      confidence = 0.85;
      reasons.push('signature_agent:chatgpt');
      detectedAgent = { type: 'chatgpt', name: 'ChatGPT' };
      verificationMethod = 'signature';
    } else if (signaturePresent) {
      confidence = Math.max(confidence, 0.4);
      reasons.push('signature_present');
    }

    // Check User-Agent patterns
    const userAgent = input.userAgent || input.headers?.['user-agent'] || '';
    if (userAgent) {
      for (const { pattern, type, name } of AI_AGENT_PATTERNS) {
        if (pattern.test(userAgent)) {
          const highConfidenceAgents = [
            'chatgpt',
            'claude',
            'perplexity',
            'anthropic',
          ];
          const patternConfidence = highConfidenceAgents.includes(type)
            ? 0.85
            : 0.5;

          confidence = Math.max(confidence, patternConfidence);
          reasons.push(`known_pattern:${type}`);
          if (!detectedAgent) {
            detectedAgent = { type, name };
            verificationMethod = 'pattern';
          }
          break;
        }
      }
    }

    // Check AI-specific headers
    const aiHeaders = [
      'openai-conversation-id',
      'openai-ephemeral-user-id',
      'anthropic-client-id',
      'x-goog-api-client',
      'x-ms-copilot-id',
    ];

    const foundAiHeaders = aiHeaders.filter(
      header => normalizedHeaders[header]
    );
    if (foundAiHeaders.length > 0) {
      confidence = Math.max(confidence, 0.6);
      reasons.push(`ai_headers:${foundAiHeaders.length}`);
    }

    // Check cloud provider IPs
    const ip = input.ip || input.ipAddress;
    if (
      ip &&
      !normalizedHeaders['x-forwarded-for'] &&
      !normalizedHeaders['x-real-ip']
    ) {
      for (const [provider, prefixes] of Object.entries(CLOUD_PROVIDERS)) {
        if (prefixes.some(prefix => ip.startsWith(prefix))) {
          confidence = Math.max(confidence, 0.4);
          reasons.push(`cloud_provider:${provider}`);
          break;
        }
      }
    }

    // Boost confidence for combinations
    if (reasons.length > 2) {
      confidence = Math.min(confidence * 1.2, 0.95);
    }

    return {
      isAgent: confidence > 0.3,
      confidence,
      ...(detectedAgent && { detectedAgent }),
      reasons,
      ...(verificationMethod && { verificationMethod }),
      forgeabilityRisk: confidence > 0.8 ? 'medium' : 'high',
      timestamp: new Date(),
    };
  }

  /**
   * Analyze request with WASM enhancement when available
   */
  async analyze(input: DetectionInput): Promise<DetectionResult> {
    // Ensure initialization
    await this.init();

    // Try WASM detection first if enabled
    if (this.wasmEnabled) {
      try {
        const wasmResult = await detectAgentWithWasm(
          input.userAgent || input.headers?.['user-agent'],
          input.headers || {},
          input.ip || input.ipAddress
        );

        if (wasmResult) {
          // WASM detection succeeded
          console.log('[AgentShield] Using WASM detection result');
          
          // Map WASM result to DetectionResult
          const detectedAgent = wasmResult.agent 
            ? this.mapAgentName(wasmResult.agent)
            : undefined;

          return {
            isAgent: wasmResult.isAgent,
            confidence: wasmResult.confidence,
            ...(detectedAgent && { detectedAgent }),
            reasons: [`wasm:${wasmResult.verificationMethod}`],
            verificationMethod: wasmResult.verificationMethod,
            forgeabilityRisk: 
              wasmResult.verificationMethod === 'signature' ? 'low' :
              wasmResult.confidence > 0.9 ? 'medium' : 'high',
            timestamp: new Date(),
          };
        }
      } catch (error) {
        console.error('[AgentShield] WASM detection error:', error);
      }
    }

    // Fall back to pattern detection
    const patternResult = await this.patternDetection(input);

    // If WASM is available but didn't detect anything, boost confidence slightly
    // to indicate WASM verification was performed
    if (this.wasmEnabled && patternResult.confidence >= 0.85) {
      patternResult.confidence = Math.min(0.95, patternResult.confidence + 0.1);
      patternResult.reasons.push('wasm_enhanced');
      if (!patternResult.verificationMethod) {
        patternResult.verificationMethod = 'wasm-enhanced';
      }
    }

    return patternResult;
  }

  /**
   * Map agent names from WASM to consistent format
   */
  private mapAgentName(agent: string): { type: string; name: string } | undefined {
    const lowerAgent = agent.toLowerCase();
    
    if (lowerAgent.includes('chatgpt')) {
      return { type: 'chatgpt', name: 'ChatGPT' };
    } else if (lowerAgent.includes('claude')) {
      return { type: 'claude', name: 'Claude' };
    } else if (lowerAgent.includes('perplexity')) {
      return { type: 'perplexity', name: 'Perplexity' };
    } else if (lowerAgent.includes('bing')) {
      return { type: 'bing', name: 'Bing AI' };
    } else if (lowerAgent.includes('anthropic')) {
      return { type: 'anthropic', name: 'Anthropic' };
    }
    
    return { type: 'unknown', name: agent };
  }
}

/**
 * Wrapper that provides event emitter functionality
 */
export class EdgeAgentDetectorWrapperWithWasm {
  private detector: EdgeAgentDetectorWithWasm;
  private events: Map<string, EventHandler[]> = new Map();

  constructor(config?: { enableWasm?: boolean; baseUrl?: string }) {
    this.detector = new EdgeAgentDetectorWithWasm(config?.enableWasm ?? true);
    if (config?.baseUrl) {
      this.detector.setBaseUrl(config.baseUrl);
    }
  }

  setBaseUrl(url: string) {
    this.detector.setBaseUrl(url);
  }

  async analyze(input: DetectionInput): Promise<DetectionResult> {
    const result = await this.detector.analyze(input);

    // Emit events if there are listeners
    if (result.isAgent && this.events.has('agent.detected')) {
      const handlers = this.events.get('agent.detected') || [];
      handlers.forEach(handler => handler(result, input));
    }

    return result;
  }

  on(event: string, handler: EventHandler): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(handler);
  }

  emit(event: string, ...args: any[]): void {
    const handlers = this.events.get(event) || [];
    handlers.forEach(handler => handler(...args));
  }

  async init(): Promise<void> {
    await this.detector.init();
  }
}